import { toDateOnly } from "@gigpro/utils/datetime";

/**
 * Mixin for the location `timezone` used to convert date fields from location-local wall-clock to
 * UTC before the wire (see the `*WithTransform` wrappers). Extend a generated request interface alongside
 * this rather than intersecting `& { timezone }` — interface extension is cached by the compiler.
 */
export interface Tz {
  timezone?: string;
}

/** Retype date-only fields `K` of request `T` as `Date`; date-only counterpart to the `Tz` mixin. */
export type DateOnlyInputs<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? Date | Exclude<T[P], string> : T[P];
};

/** Retype the time-of-day fields `K` of `T` as `Date`; same mechanics as {@link DateOnlyInputs}, for bare
 *  "HH:mm:ss" wire fields that carry no date. See `client/wrapped/shifts/shifts.ts`. */
export type TimeOnlyInputs<T, K extends keyof T> = DateOnlyInputs<T, K>;

/**
 * Build the `query.select` every read wrapper hands to its generated hook: run the mandatory `transform`
 * (raw wire payload → tz-converted shape), then chain the consumer's own `select` if they passed one.
 * `TData` defaults to the converted type, so `select: composeSelect(transform, options?.query?.select)`
 * replaces the repeated `const converted = transform(data); return userSelect ? userSelect(converted) : converted`.
 */
export const composeSelect =
  <TRaw, TConverted, TData = TConverted>(
    transform: (data: TRaw) => TConverted,
    userSelect?: (data: TConverted) => TData,
  ) =>
  (data: TRaw): TData => {
    const converted = transform(data);
    return userSelect ? userSelect(converted) : (converted as unknown as TData);
  };

/** Convert the `Date` fields `K` to "YYYY-MM-DD" wire strings. `toWire` defaults to UTC (`toDateOnly`);
 *  pass a local formatter for picker values so the day the user saw is the day that ships. */
export const toDateOnlyFields = <T extends object, K extends keyof T>(
  data: T,
  keys: ReadonlyArray<K>,
  toWire: (date: Date) => string = toDateOnly,
): { [P in keyof T]: P extends K ? string | Exclude<T[P], Date> : T[P] } => {
  const next = { ...data } as Record<PropertyKey, unknown>;
  for (const key of keys) {
    const value = next[key];
    if (value instanceof Date) next[key] = toWire(value);
  }
  return next as never;
};
